import { createConsumer } from '@rails/actioncable';
import Service from '@ember/service';

// This service manages the single ActionCable WebSocket connection for the entire app.
// Apps (tio-admin, tio-employee) should configure this service once at startup.
// Other services should only call subscribe()/unsubscribe().

type SubscriptionType = ReturnType<ReturnType<typeof createConsumer>['subscriptions']['create']>;

type SubscriptionCallbacks = {
  received?: (data: unknown) => void;
  connected?: () => void;
  disconnected?: () => void;
};

type SubscriptionRecord = {
  channelParams: object;
  callbacks: SubscriptionCallbacks;
  subscription: SubscriptionType;
};

export default class CableService extends Service {
  consumer?: ReturnType<typeof createConsumer>;

  // Track subscriptions by channel identifier, storing full record for reconnection
  private subscriptions = new Map<string, SubscriptionRecord>();

  // Connection config - set once by app initializer
  private apiHost?: string;
  private apiKey?: string;
  private token?: string;

  // Guard against concurrent reconnections
  private isReconnecting = false;

  /**
   * Configure the cable service with connection details.
   * Should be called once by the app (tio-admin/tio-employee) at startup.
   */
  configure(apiHost: string, apiKey: string, token: string) {
    this.apiHost = apiHost;
    this.apiKey = apiKey;
    this.token = token;
  }

  /**
   * Update the auth token and reconnect if already connected.
   * Automatically restores all active subscriptions with the new token.
   */
  updateToken(token: string) {
    const tokenChanged = this.token !== token;
    this.token = token;

    // If connected and token changed, reconnect with new token
    if (this.consumer && tokenChanged) {
      this.reconnectWithNewToken();
    }
  }

  /**
   * Reconnect with current token and restore all subscriptions.
   * Used when token is rotated while connection is active.
   */
  private reconnectWithNewToken() {
    if (this.isReconnecting) {
      return;
    }

    this.isReconnecting = true;

    try {
      // Save current subscription records before disconnecting
      const savedSubscriptions = new Map(this.subscriptions);

      // Disconnect consumer only (don't clear subscription records)
      if (this.consumer) {
        this.consumer.disconnect();
        this.consumer = undefined;
      }

      // Create new consumer with updated token
      if (!this.isConfigured) {
        throw new Error('CableService: Cannot reconnect - not configured');
      }

      const KEY = encodeURIComponent(this.apiKey!);
      const TOKEN = encodeURIComponent(this.token!);
      const URL = `${this.apiHost}/cable?apiKey=${KEY}&token=${TOKEN}`;
      this.consumer = createConsumer(URL);

      // Restore all subscriptions
      for (const [identifier, record] of savedSubscriptions) {
        const newSubscription = this.consumer.subscriptions.create(
          record.channelParams,
          record.callbacks
        );
        this.subscriptions.set(identifier, {
          channelParams: record.channelParams,
          callbacks: record.callbacks,
          subscription: newSubscription,
        });
      }
    } finally {
      this.isReconnecting = false;
    }
  }

  /**
   * Check if cable is configured
   */
  get isConfigured(): boolean {
    return !!(this.apiHost && this.apiKey && this.token);
  }

  /**
   * Ensure connection is established. Called automatically by subscribe().
   */
  private ensureConnected() {
    if (this.consumer) return;

    if (!this.isConfigured) {
      console.error('CableService: Not configured. Call configure() first.');
      return;
    }

    const KEY = encodeURIComponent(this.apiKey!);
    const TOKEN = encodeURIComponent(this.token!);
    const URL = `${this.apiHost}/cable?apiKey=${KEY}&token=${TOKEN}`;
    this.consumer = createConsumer(URL);
  }

  /**
   * @deprecated Use configure() at app startup instead.
   * Kept for backwards compatibility during migration.
   */
  connect(apiHost: string, apiKey: string, token: string) {
    if (!this.isConfigured) {
      this.configure(apiHost, apiKey, token);
    }
    this.ensureConnected();
  }

  /**
   * Create a subscription or return existing one if already subscribed.
   * Prevents duplicates based on channel identifier.
   * Note: If a subscription already exists, new callbacks are ignored.
   * Unsubscribe first if you need to change callbacks.
   * Automatically establishes connection if needed.
   *
   * WARNING: Do not call .unsubscribe() directly on the returned subscription
   * as it bypasses registry cleanup. Use cable.unsubscribe(channelParams) instead.
   */
  subscribe(channelParams: object, callbacks: SubscriptionCallbacks): SubscriptionType | undefined {
    this.ensureConnected();

    if (!this.consumer) {
      console.error('CableService: Cannot subscribe - not configured');
      return undefined;
    }

    const identifier = this.getIdentifier(channelParams);

    // Return existing subscription if already subscribed
    const existingRecord = this.subscriptions.get(identifier);
    if (existingRecord) {
      // Warn if new callbacks differ from existing ones (they will be ignored)
      if (
        (callbacks.received && existingRecord.callbacks.received !== callbacks.received) ||
        (callbacks.connected && existingRecord.callbacks.connected !== callbacks.connected) ||
        (callbacks.disconnected && existingRecord.callbacks.disconnected !== callbacks.disconnected)
      ) {
        console.warn(
          'CableService: subscribe() called with different callbacks for an existing subscription. ' +
            'The new callbacks will be ignored. Unsubscribe first if you want to change callbacks.'
        );
      }
      return existingRecord.subscription;
    }

    const subscription = this.consumer.subscriptions.create(channelParams, callbacks);
    this.subscriptions.set(identifier, {
      channelParams,
      callbacks,
      subscription,
    });

    return subscription;
  }

  /**
   * Generate a stable identifier for channel params.
   * Sorts object keys to ensure consistent ordering.
   */
  private getIdentifier(channelParams: object): string {
    return this.stableStringify(channelParams);
  }

  /**
   * Deterministically stringify an object by sorting its keys.
   * Handles nested objects and arrays.
   */
  private stableStringify(obj: unknown): string {
    if (obj === null || typeof obj !== 'object') {
      return JSON.stringify(obj);
    }
    if (Array.isArray(obj)) {
      return '[' + obj.map((item) => this.stableStringify(item)).join(',') + ']';
    }
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    const keyValuePairs = keys.map((key) => {
      const value = (obj as Record<string, unknown>)[key];
      return JSON.stringify(key) + ':' + this.stableStringify(value);
    });
    return '{' + keyValuePairs.join(',') + '}';
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channelParams: object) {
    const identifier = this.getIdentifier(channelParams);
    const record = this.subscriptions.get(identifier);

    if (record) {
      record.subscription.unsubscribe();
      this.subscriptions.delete(identifier);
    }
  }

  /**
   * Perform an action on a subscription
   * @param channelParams - The channel params used when subscribing
   * @param action - The action name to perform
   * @param data - The data to send with the action
   */
  perform(channelParams: object, action: string, data?: object): boolean {
    const identifier = this.getIdentifier(channelParams);
    const record = this.subscriptions.get(identifier);

    if (record) {
      record.subscription.perform(action, data || {});
      return true;
    }

    console.warn('CableService: No subscription found for perform()', channelParams);
    return false;
  }

  /**
   * Disconnect and clean up all subscriptions
   */
  disconnect() {
    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = undefined;
      this.subscriptions.clear();
    }
  }
}
